import { IconButton, type IconButtonProps } from '@chakra-ui/react';

import { useTranslation } from 'react-i18next';
import { Star } from 'iconoir-react';

interface WishlistToggleButtonProps extends Omit<IconButtonProps, 'aria-label'> {
  isInWishlist: boolean;
}

/**
 * A presentational button component for toggling wishlist status.
 * Shows a star icon that changes between filled (in wishlist) and outline (not in wishlist).
 *
 * Uses Chakra UI IconButton with consistent styling.
 * All logic (mutations, callbacks) should be handled by the parent component.
 */
const WishlistToggleButton = ({ isInWishlist, ...rest }: WishlistToggleButtonProps) => {
  const { t } = useTranslation();

  return (
    <IconButton
      aria-label={
        isInWishlist
          ? t('creatorDiscovery.card.removeFromWishlist')
          : t('creatorDiscovery.card.addToWishlist')
      }
      variant="outline"
      size="sm"
      rounded="lg"
      borderColor={isInWishlist ? 'yellow.400' : 'gray.200'}
      color={isInWishlist ? 'yellow.500' : 'gray.500'}
      bg={isInWishlist ? 'yellow.50' : 'transparent'}
      _hover={{
        borderColor: isInWishlist ? 'yellow.500' : 'gray.300',
        color: isInWishlist ? 'yellow.600' : 'gray.700',
        bg: isInWishlist ? 'yellow.100' : 'gray.50',
      }}
      {...rest}
    >
      <Star />
    </IconButton>
  );
};

export default WishlistToggleButton;
